/* istanbul ignore file */

import type { TouchEvent } from 'react';
import React, { useState } from 'react';
import type { GetItemPropsOptions, GetMenuPropsOptions, GetPropsCommonOptions } from 'downshift';

import type { AddressSearchResult } from 'src/components/molecules/AddressSearch/services/loqate';
import { SEARCH_RESULT_TYPE_ADDRESS } from 'src/components/molecules/AddressSearch/services/loqate';
import { FakeLink } from './resultsComponents/FakeLink';
import { Hint } from './resultsComponents/Hint';
import { Result } from './resultsComponents/Result';
import { ResultsPane } from './resultsComponents/ResultsPane';
import { ResultSubtitle } from './resultsComponents/ResultSubtitle';
import { ResultTitle } from './resultsComponents/ResultTitle';

const LABELS = {
  TYPING_HINT: 'Keep typing for more accurate results',
  BACK_BUTTON: <span>&#x2B05; Back</span>,
};

const SEARCH_RESULT_TYPE_FAKE = 'fakeSearchResult';
const containerFakeLink: AddressSearchResult = {
  Id: '',
  Type: SEARCH_RESULT_TYPE_FAKE,
  Text: '',
  Highlight: '',
  Description: '',
};

export interface ResultsProps {
  containerId: string;
  forwardRef: React.RefObject<ResultsType>;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  getItemProps: (options: GetItemPropsOptions<AddressSearchResult>) => any;
  getMenuProps: (
    options?: GetMenuPropsOptions | undefined,
    otherOptions?: GetPropsCommonOptions | undefined,
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
  ) => any;
  highlightedIndex: number | null;
  onClearContainer: () => void;
  onSelect: (item: AddressSearchResult | null) => void;
  results: AddressSearchResult[] | null;
  selectedItem: AddressSearchResult | null;
  showClear: boolean;
  showTypingHint: boolean;
}

// eslint-disable-next-line @typescript-eslint/no-empty-interface
export interface ResultsType extends HTMLUListElement {}

export const Results = ({
  containerId,
  forwardRef,
  getItemProps,
  getMenuProps,
  highlightedIndex,
  onClearContainer,
  onSelect,
  results: resultsProp,
  showClear,
  showTypingHint,
}: ResultsProps) => {
  const [isDragging, setIsDragging] = useState(false);
  const results = [
    ...(showClear && containerId.length ? [containerFakeLink] : []),
    ...(resultsProp ? resultsProp : []),
  ];

  const handleTouchEnd = (e: TouchEvent<HTMLLIElement>, res: AddressSearchResult) => {
    e.preventDefault();
    setIsDragging(false);

    return !isDragging && onSelect(res);
  };

  const menuProps = getMenuProps({
    ref: forwardRef,
  });

  const getResultItemProps = (res: AddressSearchResult, index: number) => ({
    isContainer:
      res.Type != null &&
      res.Type !== SEARCH_RESULT_TYPE_FAKE &&
      res.Type !== SEARCH_RESULT_TYPE_ADDRESS,
    isHighlighted: index === highlightedIndex,
    item: res,
    key: res.Id || -index,
    onTouchEnd: (e: TouchEvent<HTMLLIElement>) => handleTouchEnd(e, res),
    onTouchMove: () => setIsDragging(true),
  });

  return (
    /* The onTouchEnd preventDefault and onTouchMove handlers lets the input
      stay focused (and therefore results panel stay open) while you interact
      with the results panel */
    <ResultsPane data-testid="address-search-results" ref={forwardRef} {...menuProps}>
      {showTypingHint && (
        <Result isResult={false}>
          <Hint>{LABELS.TYPING_HINT}</Hint>
        </Result>
      )}
      {results.map((res, index) => (
        <Result key={res.Id} {...getItemProps(getResultItemProps(res, index))}>
          {index === 0 && showClear ? (
            <FakeLink onMouseUp={onClearContainer}>{LABELS.BACK_BUTTON}</FakeLink>
          ) : (
            <>
              <ResultTitle>{res.Text}</ResultTitle>
              <ResultSubtitle>{res.Description}</ResultSubtitle>
            </>
          )}
        </Result>
      ))}
    </ResultsPane>
  );
};
